/**
 * Admin Layout Component
 * Provides layout structure for admin pages with sidebar and header
 */

import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    FiSettings,
    FiUsers,
    FiFileText,
    FiBarChart2,
    FiHome,
    FiBell
} from 'react-icons/fi';
import useScrollToTop from '@/hooks/useScrollToTop';

interface AdminLayoutProps {
    children?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
    useScrollToTop();
    const navigate = useNavigate();
    const location = useLocation();

    const navigationItems = [
        {
            name: 'Dashboard',
            path: '/admin',
            icon: FiHome
        },
        {
            name: 'Document Verification',
            path: '/admin/verification',
            icon: FiFileText
        },
        {
            name: 'Notification Analytics',
            path: '/admin/analytics/notifications',
            icon: FiBarChart2
        },
        {
            name: 'User Management',
            path: '/admin/users',
            icon: FiUsers
        },
        {
            name: 'System Settings',
            path: '/admin/settings',
            icon: FiSettings
        }
    ];

    const isActive = (path: string) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="flex">
                {/* Sidebar */}
                <div className="w-64 bg-white dark:bg-gray-800 shadow-lg">
                    <div className="p-6">
                        <div className="flex items-center gap-3 mb-8">
                            <FiBell className="w-8 h-8 text-blue-600" />
                            <div>
                                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                                    SideGO Admin
                                </h1>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Management Panel
                                </p>
                            </div>
                        </div>

                        <nav className="space-y-2">
                            {navigationItems.map((item) => (
                                <button
                                    key={item.path}
                                    onClick={() => navigate(item.path)}
                                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${isActive(item.path)
                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    <item.icon className="w-5 h-5" />
                                    {item.name}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {/* Main Content */}
                <main className="flex-1 p-6 overflow-y-auto">
                    {children || <Outlet />}
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;